import { useRef, useState } from "react";
import { Upload, Download, Trash2 } from "lucide-react";
import WorkingHoursEditor from "../components/WorkingHoursEditor";
import Modal from "../components/Modal";
import { downloadConfigBackup, useClearLogs, useImportConfig } from "../api/queries";
import { ApiError } from "../api/client";
import type { BackupFile } from "../api/types";

type PreviewState =
  | { kind: "idle" }
  | { kind: "parsing" }
  | { kind: "invalid"; message: string }
  | { kind: "ready"; file: BackupFile; filename: string };

export default function ConfigPage() {
  const [exportError, setExportError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>({ kind: "idle" });
  const [importStatus, setImportStatus] = useState<null | { kind: "ok" | "err"; text: string }>(null);
  const [logStatus, setLogStatus] = useState<null | { kind: "ok" | "err"; text: string }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMut = useImportConfig();
  const clearLogsMut = useClearLogs();

  async function onClearLogs() {
    setLogStatus(null);
    if (!confirm("Clear the recent activity log? This cannot be undone.")) return;
    try {
      await clearLogsMut.mutateAsync();
      setLogStatus({ kind: "ok", text: "Activity log cleared." });
    } catch (err) {
      const detail = err instanceof ApiError ? String(err.detail ?? err.message) : String(err);
      setLogStatus({ kind: "err", text: detail });
    }
  }

  async function onExport() {
    setExportError(null);
    try {
      await downloadConfigBackup();
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onFileChosen(file: File) {
    setImportStatus(null);
    setPreview({ kind: "parsing" });
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      if (!isBackupFile(parsed)) {
        setPreview({ kind: "invalid", message: "File is not a valid backup (missing version/presets/rules/working_hours)." });
        return;
      }
      if (parsed.version !== 1 && parsed.version !== 2) {
        setPreview({ kind: "invalid", message: `Unsupported version: ${parsed.version}` });
        return;
      }
      setPreview({ kind: "ready", file: parsed, filename: file.name });
    } catch (err) {
      setPreview({
        kind: "invalid",
        message: err instanceof Error ? `Parse error: ${err.message}` : String(err),
      });
    }
  }

  async function onConfirmImport() {
    if (preview.kind !== "ready") return;
    setImportStatus(null);
    try {
      const res = await importMut.mutateAsync(preview.file);
      setImportStatus({
        kind: "ok",
        text: `Imported ${res.counts.presets} presets, ${res.counts.rules} rules, ${res.counts.working_hours} working-hours rows.`,
      });
      setPreview({ kind: "idle" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const detail = err instanceof ApiError ? String(err.detail ?? err.message) : String(err);
      setImportStatus({ kind: "err", text: detail });
    }
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold">Working hours</h2>
          <p className="text-xs text-slate-400">Per-weekday reference schedule in 24-hour time.</p>
        </header>
        <WorkingHoursEditor />
      </section>

      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold">Activity log</h2>
          <p className="text-xs text-slate-400">
            In-memory ring buffer (last ~200 events) shown on the status page. Clearing wipes it immediately; new events resume on the next tick.
          </p>
        </header>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void onClearLogs()}
            disabled={clearLogsMut.isPending}
            className="btn-danger inline-flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {clearLogsMut.isPending ? "Clearing…" : "Clear activity log"}
          </button>
        </div>
        <div className="min-h-[1.25rem] text-sm">
          {logStatus ? (
            <span className={logStatus.kind === "ok" ? "text-emerald-400" : "text-rose-400"}>
              {logStatus.text}
            </span>
          ) : null}
        </div>
      </section>

      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold">Backup</h2>
          <p className="text-xs text-slate-400">
            Export and import all rules, presets, and working hours. Import replaces everything.
          </p>
        </header>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => void onExport()} className="btn-secondary inline-flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export backup
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Import backup…
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFileChosen(file);
            }}
          />
        </div>

        <div className="min-h-[1.25rem] text-sm">
          {exportError ? <span className="text-rose-400">Export failed: {exportError}</span> : null}
          {importStatus ? (
            <span className={importStatus.kind === "ok" ? "text-emerald-400" : "text-rose-400"}>
              {importStatus.text}
            </span>
          ) : null}
        </div>
      </section>

      <Modal
        open={preview.kind === "ready" || preview.kind === "invalid" || preview.kind === "parsing"}
        onClose={() => {
          setPreview({ kind: "idle" });
          if (fileInputRef.current) fileInputRef.current.value = "";
        }}
        title="Import backup"
        size="md"
      >
        {preview.kind === "parsing" ? (
          <p className="text-sm text-slate-400">Parsing…</p>
        ) : preview.kind === "invalid" ? (
          <p className="text-sm text-rose-400">{preview.message}</p>
        ) : preview.kind === "ready" ? (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-300">
                <span className="font-medium">{preview.filename}</span> — exported{" "}
                {new Date(preview.file.exported_at).toLocaleString()}
              </p>
              <ul className="mt-3 space-y-1 text-sm text-slate-300">
                <li>Presets: <span className="font-medium">{preview.file.presets.length}</span></li>
                <li>Rules: <span className="font-medium">{preview.file.rules.length}</span></li>
                <li>Working hours: <span className="font-medium">{preview.file.working_hours.length}</span></li>
              </ul>
            </div>
            <div className="rounded-md border border-rose-900/60 bg-rose-950/40 p-3 text-xs text-rose-200">
              This will <strong>replace</strong> all existing rules, presets, and working hours. The change is atomic but not undoable.
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setPreview({ kind: "idle" });
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onConfirmImport()}
                disabled={importMut.isPending}
                className="btn-danger"
              >
                {importMut.isPending ? "Importing…" : "Confirm replace"}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function isBackupFile(value: unknown): value is BackupFile {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.version === "number" &&
    typeof v.exported_at === "string" &&
    Array.isArray(v.presets) &&
    Array.isArray(v.rules) &&
    Array.isArray(v.working_hours)
  );
}
